import { DateTime } from "luxon";
import { useMemo } from "react";
import { CalendarGrid } from "@/domains/appointment/appointments/components/calendar/CalendarGrid";
import {
  OperatingHoursOverlay,
  useOperatingHoursForDate,
} from "@/domains/appointment/appointments/components/calendar/operating-hours-overlay";
import { TimeSlotGrid } from "@/domains/appointment/appointments/components/calendar/TimeSlotGrid";
import {
  createTimeSlotConfig,
  getCalendarThemeConfig,
  getTimeColumnWidth,
} from "@/domains/appointment/appointments/config/calendar-config";
import {
  calculateAppointmentPosition,
  calculateExtendedTimeRange,
  computeAppointmentLayoutMap,
  isToday,
} from "@/domains/appointment/appointments/utils/calendar-utils";
import { CalendarAppointmentCard } from "@/domains/appointment/calendar/components/calendar-appointment-card";
import { useOperatingHours } from "@/domains/settings/hooks/use-operating-hours";
import type { AppointmentBookingStatus } from "@/gql/graphql";
import { useSalonStore } from "@/lib/store/salon";
import { useCalendarAppointments } from "../../hooks/use-calendar-appointments";
import { useCalendarFilters } from "../../hooks/use-calendar-filters";
import { useCalendarStateStore } from "../../store/use-calendar-state-store";
import type { AppointmentListFragment } from "../../types";
import { CurrentTimeLine } from "../ui/current-time-line";

interface DayViewProps {
  currentDate: DateTime;
  onAppointmentClick?: (appointmentId: string) => void;
}

export function DayView({ currentDate, onAppointmentClick }: DayViewProps) {
  // Ensure we have a valid date
  const luxonDate = currentDate?.isValid ? currentDate : DateTime.now();

  const { currentSalon } = useSalonStore();
  const { statusFilters, workerFilters } = useCalendarFilters();
  const { timeScale } = useCalendarStateStore();

  // Fetch operating hours
  const { operatingHours } = useOperatingHours(currentSalon?.id);

  // Get operating hours for this specific date
  const dayOperatingHours = useOperatingHoursForDate(operatingHours, luxonDate);

  // Fetch appointments for the current day
  const { appointments } = useCalendarAppointments({
    startDate: luxonDate.startOf("day"),
    endDate: luxonDate.endOf("day"),
    statusFilters: statusFilters as AppointmentBookingStatus[],
    workerFilters,
  });

  // Calculate extended time range based on operating hours AND appointments
  const { startHour, endHour } = useMemo(() => {
    return calculateExtendedTimeRange(
      dayOperatingHours.map((h) => ({ openTime: h.startTime, closeTime: h.endTime })),
      appointments
    );
  }, [dayOperatingHours, appointments]);

  // Configuration for time slots and positioning using dynamic scale
  const scaleConfig = getCalendarThemeConfig(timeScale);
  const safeStartHour = !Number.isNaN(startHour) ? startHour : 6;
  const safeEndHour = !Number.isNaN(endHour) ? endHour : 23;
  const timeConfig = createTimeSlotConfig(safeStartHour, safeEndHour, {
    hourHeight: scaleConfig.layout.hourHeight,
  });

  // Use all appointments since they are already filtered by the hook for the current day
  const dayAppointments = appointments;

  // Optimized: Calculate layout once for all appointments (Google Calendar style)
  const positionConfig = useMemo(
    () => ({
      hourHeight: scaleConfig.layout.hourHeight,
      minHeight: scaleConfig.layout.minAppointmentHeight,
    }),
    [scaleConfig.layout.hourHeight, scaleConfig.layout.minAppointmentHeight]
  );

  // Single calculation for all appointments instead of per-appointment
  const layoutMap = useMemo(() => {
    return computeAppointmentLayoutMap(dayAppointments, positionConfig, safeStartHour);
  }, [dayAppointments, positionConfig, safeStartHour]);

  const positionedAppointments = useMemo(() => {
    return dayAppointments.map((appointment) => {
      const position = calculateAppointmentPosition(appointment, positionConfig, safeStartHour);
      const layout = layoutMap.get(appointment.id) ?? {
        width: "calc(100% - 6px)",
        left: "3px",
        hasOverlap: false,
        overlapCount: 1,
        zIndex: 1,
      };

      return {
        appointment,
        style: {
          position: "absolute" as const,
          top: `${position.top}px`,
          height: `${Math.max(position.height, positionConfig.minHeight || 24)}px`,
          left: layout.left,
          width: layout.width,
          zIndex: layout.zIndex,
        },
        hasOverlap: layout.hasOverlap,
        overlapCount: layout.overlapCount,
      };
    });
  }, [dayAppointments, layoutMap, positionConfig, safeStartHour]);

  const handleAppointmentClick = (appointment: AppointmentListFragment) => {
    onAppointmentClick?.(appointment.id);
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Scrollable Calendar Content with sticky header */}
      <div className="flex-1 overflow-auto">
        {/* Day Header - Sticky */}
        <div className="sticky top-0 z-50 border border-border rounded-t-lg bg-card shadow-sm overflow-hidden bg-clip-padding">
          <div className="flex">
            <div
              className={`${getTimeColumnWidth()} flex-shrink-0 h-12 border-r border-border bg-muted flex items-center justify-center`}
            >
              <span className="text-xs font-medium text-muted-foreground">Time</span>
            </div>
            <div className="flex-1">
              <div className="h-12 flex flex-col items-center justify-center bg-muted">
                <div className="text-xs text-muted-foreground">{luxonDate.toFormat("ccc")}</div>
                <div className="text-sm font-medium text-foreground font-bold">
                  {luxonDate.toFormat("d")}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Calendar Content */}
        <CalendarGrid
          timeConfig={timeConfig}
          className="min-h-[600px] border-0 border-l border-r border-b border-border rounded-b-lg"
          minHeight="600px"
          timeColumnWidth={getTimeColumnWidth()}
        >
          {/* Day Column */}
          <div className="relative">
            {/* Time Slots with Appointments */}
            <TimeSlotGrid
              config={timeConfig}
              onDragOver={() => {}}
              onDragLeave={() => {}}
              onDrop={() => {}}
            >
              {/* Operating Hours Overlay */}
              <OperatingHoursOverlay
                operatingHours={dayOperatingHours}
                startTime={luxonDate
                  .startOf("day")
                  .set({ hour: !Number.isNaN(startHour) ? startHour : 6 })}
                endTime={luxonDate
                  .startOf("day")
                  .set({ hour: !Number.isNaN(endHour) ? endHour : 23 })}
                hourHeight={scaleConfig.layout.hourHeight}
                showLabels={true}
                closedOpacity={0.25}
                className="z-0"
              />

              {/* Current Time Line - only show if viewing today */}
              {(() => {
                const isTodayView = isToday(luxonDate);

                return (
                  isTodayView && (
                    <CurrentTimeLine
                      startHour={!Number.isNaN(startHour) ? startHour : 0}
                      endHour={!Number.isNaN(endHour) ? endHour : 24}
                      hourHeight={scaleConfig.layout.hourHeight}
                    />
                  )
                );
              })()}

              {/* Appointments */}
              {positionedAppointments.map(({ appointment, style, hasOverlap }) => (
                <CalendarAppointmentCard
                  key={appointment.id}
                  appointment={appointment}
                  height={Number.parseInt(style.height?.toString().replace("px", "") || "24", 10)}
                  pixelsPerHour={scaleConfig.layout.hourHeight}
                  style={style}
                  className={`${hasOverlap ? "shadow-lg border" : "shadow-sm"} transition-shadow hover:shadow-lg`}
                  onClick={handleAppointmentClick}
                />
              ))}
            </TimeSlotGrid>
          </div>
        </CalendarGrid>
      </div>
    </div>
  );
}
